import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Section } from "@/components/app/Primitives";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const StatCard = ({ label, value, hint, tone = "slate", testId }) => (
  <Card className={`border-${tone}-200`} data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`text-3xl font-heading font-black text-${tone}-800`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </CardContent>
  </Card>
);

export default function EYDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/ey/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-500">Loading EY dashboard…</div>;

  return (
    <div className="space-y-6" data-testid="ey-dashboard">
      <Section
        title="EY Audit Console"
        subtitle={`On behalf of ${data.scoped_tenants?.join(", ") || "no tenants scoped"} — verify KYS, review deliverables, approve to release payment.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Campaigns in scope" value={data.campaign_count} tone="indigo" testId="stat-campaigns" />
        <StatCard label="Pending EY reviews" value={data.pending_ey_reviews} hint="Awaiting audit approval" tone="rose" testId="stat-pending" />
        <StatCard label="Deliverables approved" value={data.deliverables_ey_approved} hint={`of ${data.deliverables_expected} expected`} tone="emerald" testId="stat-approved" />
        <StatCard label="Deliverables submitted" value={data.deliverables_submitted} hint="By influencers" tone="amber" testId="stat-submitted" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">KYS Pipeline</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data.kys_pipeline || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="capitalize">{k.replace(/_/g, " ")}</span>
                <Badge variant={k === "verified" ? "default" : "outline"}>{v}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Orders by status</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data.orders_by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="capitalize">{k.replace(/_/g, " ")}</span>
                <Badge variant="outline">{v}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Section title="Recent deliverables awaiting your review" subtitle={`${data.recent_pending?.length || 0} shown · newest first`}>
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Deliverable</th>
                <th className="px-4 py-3 text-left">Campaign</th>
                <th className="px-4 py-3 text-left">Influencer</th>
                <th className="px-4 py-3 text-left">Submitted</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_pending || []).map((d) => (
                <tr key={d.id} className="border-t hover:bg-slate-50" data-testid={`ey-pending-row-${d.id}`}>
                  <td className="px-4 py-3">{d.deliverable_type || d.type}</td>
                  <td className="px-4 py-3">{d.campaign}</td>
                  <td className="px-4 py-3">{d.influencer_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(d.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/ey/deliverables`}>
                      <Button size="sm" className="bg-[#0052FF] hover:bg-[#0043D9]" data-testid={`ey-review-${d.id}`}>Review</Button>
                    </Link>
                  </td>
                </tr>
              ))}
              {!data.recent_pending?.length && (
                <tr><td colSpan={5} className="text-center text-sm text-slate-500 py-8">No pending EY reviews · you're all caught up.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
